import { CrownSimple as CrownSimpleIcon } from '@phosphor-icons/react';
import React, { useState, useEffect, useRef } from 'react';
import {
  ChartBar,
  Star
} from '@phosphor-icons/react';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import 'leaflet.heat';
import { AuthContext } from '../../../context/authContext.jsx';
import { useContext } from 'react';
import { Navigate } from 'react-router-dom';
import { useTaskTypes } from '../../../hooks/useTaskTypes.js';
import GenericFilters from '../../../shared/components/ui/GenericFilters.jsx';
import PageHeader from '../../../shared/components/ui/PageHeader.jsx';
import { teamService } from '../../../services/teams/teamService.js';
import DensityMap from '../components/DensityMap.jsx';
import DonutChart from '../components/DonutChart.jsx';
import BarChartMulti from '../components/BarChartMulti.jsx';
import CardsStatistics from '../components/CardsStatistics.jsx';
import { adminHomeService } from '../services/adminHomeService.js';
import { getDataColor } from '../../../styles/dataColors.js';


// Fix para los iconos de Leaflet
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});

const AdminHome = () => {
  const [loadingCards, setLoadingCards] = useState(true);
  const [loadingDonut, setLoadingDonut] = useState(true);
  const [loadingBarTeam, setLoadingBarTeam] = useState(true);
  const [loadingBarType, setLoadingBarType] = useState(true);
  const [loadingMap, setLoadingMap] = useState(true);
  const [loadingRanking, setLoadingRanking] = useState(true);
  const [error, setError] = useState(null);
  const [statistics, setStatistics] = useState({ totalTasks: 0, tasksByStatus: [] });
  const [teamPerformance, setTeamPerformance] = useState([]);
  const [taskTypeCounts, setTaskTypeCounts] = useState([]);
  const [dataDensityMap, setDataDensityMap] = useState([]);
  const [rankingTeam, setRankingTeam] = useState([]);
  const [technicians, setTechnicians] = useState([]);
  const [teams, setTeams] = useState([]);
  const { taskTypes } = useTaskTypes();
  const {isAuthenticated} = useContext(AuthContext)
  
  // Filtros
  const [filters, setFilters] = useState({
    teamId: '',
    typeTask: '',
    dateRange: 'week', // week, month, year, period
    dateFrom: '',
    dateTo: ''
  });
  
  useEffect(() => {
    fetchTeams();
  }, []);

  useEffect(() => {
    // Un período sin fechas todavía se está configurando: no consultar hasta
    // que el usuario indique al menos un límite del rango.
    if (filters.dateRange === 'period' && !filters.dateFrom && !filters.dateTo) {
      return;
    }

    fetchCardsDonut();
    fetchBarTeam();
    fetchBarType();
    fetchDensityMap();
    fetchRankingTeam();
  }, [filters]);

  const fetchTeams = async () => {
    try {
      const teamsRes = await teamService.getTeams();
      setTeams(teamsRes);
    } catch (err) {
      setError(err.message);
    }
  };

  // Carga independiente para cada sección
  const fetchCardsDonut = async () => {
    try {
      setLoadingCards(true);
      setLoadingDonut(true);
      setError(null);
      const statisticsRes = await adminHomeService.getTaskStatistics(filters);
      setStatistics(statisticsRes);
      setLoadingCards(false);
      setLoadingDonut(false);
    } catch (err) {
      console.error('Error al cargar estadísticas:', err);
      // NO cambiar los estados de loading para mantener el skeleton
    }
  };

  const fetchBarTeam = async () => {
    try {
      setLoadingBarTeam(true);
      setError(null);
      const teamPerformanceRes = await adminHomeService.getTeamPerformance(filters);
      let teamName = 'Todos los equipos';
      if (filters.teamId && teams.length > 0) {
        const selectedTeam = teams.find(team => String(team.id) === String(filters.teamId));
        teamName = selectedTeam ? `Equipo ${selectedTeam.name}` : `Equipo ${filters.teamId}`;
      }
      setTeamPerformance([{
        name: teamName,
        tareas: teamPerformanceRes.totalTasks,
        completadas: teamPerformanceRes.completedTasks
      }]);
      setLoadingBarTeam(false);
    } catch (err) {
      console.error('Error al cargar rendimiento de equipos:', err);
      // NO cambiar el estado de loading para mantener el skeleton
    }
  };

  const fetchBarType = async () => {
    try {
      setLoadingBarType(true);
      setError(null);
      const taskTypeCountsRes = await adminHomeService.getTaskTypeCounts(filters);
      setTaskTypeCounts(taskTypeCountsRes);
      setLoadingBarType(false);
    } catch (err) {
      console.error('Error al cargar tipos de tareas:', err);
      // NO cambiar el estado de loading para mantener el skeleton
    }
  };

  const fetchDensityMap = async () => {
    try {
      setLoadingMap(true);
      setError(null);
      const tasksRes = await adminHomeService.getDensityMap(filters);
      setDataDensityMap(tasksRes);
      setLoadingMap(false);
    } catch (err) {
      console.error('Error al cargar mapa de densidad:', err);
      // NO cambiar el estado de loading para mantener el skeleton
    }
  };

  const fetchRankingTeam = async () => {
    try {
      setLoadingRanking(true);
      setError(null);
      const rankingRes = await adminHomeService.getTeamRanking(filters);
      setRankingTeam(rankingRes);
      setLoadingRanking(false);
    } catch (err) {
      console.error('Error al cargar rendimiento de equipos:', err);
      // NO cambiar el estado de loading para mantener el skeleton
    }
  };

  const clearFilters = () => {
    setFilters({
      teamId: '',
      typeTask: '',
      dateRange: 'week',
      dateFrom: '',
      dateTo: ''
    });
  };

  const handleFilterChange = (nextFilters) => {
    // Las fechas manuales solo aplican al período personalizado.
    if (nextFilters.dateRange !== 'period') {
      setFilters({ ...nextFilters, dateFrom: '', dateTo: '' });
      return;
    }

    setFilters(nextFilters);
  };

  const filterColumns = [
    {
      type: 'select',
      key: 'teamId',
      label: 'Equipo',
      options: [
        { value: '', label: 'Todos los equipos' },
        ...teams.map(team => ({ value: team.id, label: `${team.name}` }))
      ],
      optionValue: 'value',
      optionLabel: 'label',
      colSpan: 1
    },
    {
      type: 'select',
      key: 'typeTask',
      label: 'Tipo de tarea',
      options: [
        { value: '', label: 'Todos los tipos' },
        ...taskTypes.map(taskType => ({
          value: taskType.name,
          label: taskType.displayName || taskType.name
        }))
      ],
      optionValue: 'value',
      optionLabel: 'label',
      colSpan: 1
    },
    {
      type: 'select',
      key: 'dateRange',
      label: 'Fecha',
      options: [
        { value: 'week', label: 'Última semana' },
        { value: 'month', label: 'Este mes' },
        { value: 'year', label: 'Este año' },
        { value: 'period', label: 'Período' }
      ],
      optionValue: 'value',
      optionLabel: 'label',
      colSpan: 1
    },
    {
      type: 'date',
      key: 'dateFrom',
      label: 'Fecha desde',
      clearable: true,
      maxDateKey: 'dateTo',
      popupPlacement: 'bottom',
      colSpan: 1,
      showWhen: (currentFilters) => currentFilters.dateRange === 'period'
    },
    {
      type: 'date',
      key: 'dateTo',
      label: 'Fecha hasta',
      clearable: true,
      minDateKey: 'dateFrom',
      popupPlacement: 'bottom',
      colSpan: 1,
      showWhen: (currentFilters) => currentFilters.dateRange === 'period'
    }
  ];

  const totalInProgress = (statistics?.tasksByStatus.find(item => item.status === 'iniciada')?.count || 0) + (statistics?.tasksByStatus.find(item => item.status === 'en_camino')?.count || 0) + (statistics?.tasksByStatus.find(item => item.status === 'relevada')?.count || 0);
  const rateInProgress = 0 + (statistics?.tasksByStatus.find(item => item.status === 'iniciada')?.rate || 0) + (statistics?.tasksByStatus.find(item => item.status === 'en_camino')?.rate || 0) + (statistics?.tasksByStatus.find(item => item.status === 'relevada')?.rate || 0);
  const sectionLoadings = [loadingCards, loadingDonut, loadingBarTeam, loadingBarType, loadingMap, loadingRanking];
  const filtersLoading = sectionLoadings.some(Boolean);

  const cardsData = [
    {
      id: 'total-tareas',
      title: 'Total Tareas',
      value: statistics?.totalTasks,
      layoutClassName: 'col-span-2 aspect-[4/1] min-[500px]:aspect-[11/2] sm:aspect-[8/1] md:!p-4 lg:col-span-1 lg:aspect-auto lg:!p-6',
      containerClassName: 'bg-white/10 border border-white/30 text-white',
      titleClassName: 'text-white/70'
    },
    {
      id: 'finalizadas',
      title: 'Finalizadas',
      value: statistics?.tasksByStatus?.find(item => item.status === 'finalizada')?.count || 0,
      progressLabel: 'Finalizadas',
      progressValue: `${statistics?.tasksByStatus.find(item => item.status === 'finalizada')?.rate || 0}%`,
      progress: statistics?.tasksByStatus.find(item => item.status === 'finalizada')?.rate || 0,
      containerClassName: 'bg-success/10 border border-success/30 text-success',
      titleClassName: 'text-success/70',
      progressTrackClassName: 'bg-success/15',
      progressBarClassName: 'bg-success'
    },
    {
      id: 'completadas',
      title: 'En progreso',
      value: totalInProgress,
      progressLabel: 'En progreso',
      progressValue: `${rateInProgress}%`,
      progress: rateInProgress,
      containerClassName: 'bg-info/10 border border-info/30 text-info',
      titleClassName: 'text-info/70',
      progressTrackClassName: 'bg-info/15',
      progressBarClassName: 'bg-info'
    },
    {
      id: 'pendientes',
      title: 'Pendientes',
      value: statistics?.tasksByStatus?.find(item => item.status === 'pendiente')?.count || 0,
      progressLabel: 'Pendientes',
      progressValue: `${statistics?.tasksByStatus.find(item => item.status === 'pendiente')?.rate || 0}%`,
      progress: statistics?.tasksByStatus.find(item => item.status === 'pendiente')?.rate || 0,
      containerClassName: 'bg-warning/10 border border-warning/30 text-warning',
      titleClassName: 'text-warning/70',
      progressTrackClassName: 'bg-warning/15',
      progressBarClassName: 'bg-warning'
    },
    {
      id: 'canceladas',
      title: 'Canceladas',
      value: statistics?.tasksByStatus?.find(item => item.status === 'cancelada')?.count || 0,
      progressLabel: 'Canceladas',
      progressValue: `${statistics?.tasksByStatus.find(item => item.status === 'cancelada')?.rate || 0}%`,
      progress: statistics?.tasksByStatus.find(item => item.status === 'cancelada')?.rate || 0,
      containerClassName: 'bg-error/10 border border-error/30 text-error',
      titleClassName: 'text-error/70',
      progressTrackClassName: 'bg-error/15',
      progressBarClassName: 'bg-error'
    }
  ];

  if (!isAuthenticated) {
    return (
      <div className="container">
        <Navigate to="/admin/login" replace />
      </div>
    );
  }

  return (
    <div className="bg-bg min-h-screen">
      <div data-tour="home-header">
        <PageHeader
          title="Inicio"
          subtitle="Vista general de métricas y estadísticas"
          icon={<ChartBar size={28} className="text-content-muted" />}
          className="mb-8"
        />
      </div>

      <div data-tour="home-kpis">
        <CardsStatistics loading={loadingCards} items={cardsData} />
      </div>

      <div data-tour="home-filters">
        <GenericFilters
          columns={filterColumns}
          filters={filters}
          onFilterChange={handleFilterChange}
          onClearFilters={clearFilters}
          defaultOpen={false}
          loading={filtersLoading}
        />
      </div>

      <div data-tour="home-charts" className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
        <div className="bg-surface rounded-xl shadow-lg border border-line p-6">
          {loadingDonut ? (
            <div className="animate-pulse">
              <div className="h-6 w-40 rounded bg-surface-2 mb-6"></div>
              <div className="h-[300px] w-full rounded-lg bg-surface-2"></div>
            </div>
          ) : (
            <>
              <h3 className="text-lg font-medium text-content mb-6">Estado de Tareas</h3>
              <DonutChart
                data={(() => {
                // Agrupar estados de progreso
                const statusMap = {
                  cancelada: { name: 'Cancelada', value: 0, color: '#EF4444' },
                  pendiente: { name: 'Pendiente', value: 0, color: '#F59E0B' },
                  en_progreso: { name: 'En progreso', value: 0, color: '#3B82F6' },
                  finalizada: { name: 'Finalizada', value: 0, color: '#10B981' }
                };
                (statistics?.tasksByStatus || []).forEach(item => {
                  if (["iniciada", "relevada", "en_camino"].includes(item.status)) {
                    statusMap.en_progreso.value += item.count;
                  } else if (item.status === "cancelada") {
                    statusMap.cancelada.value += item.count;
                  } else if (item.status === "pendiente") {
                    statusMap.pendiente.value += item.count;
                  } else if (item.status === "finalizada") {
                    statusMap.finalizada.value += item.count;
                  }
                });
                return Object.entries(statusMap)
                  .filter(([_, item]) => item.value > 0)
                  .map(([_, item]) => item);
              })()}
              useEntryColor
              height={300}
              innerRadius={60}
              outerRadius={100}
              isAnimationActive={false}
              emptyMessage="No hay tareas en el período seleccionado"
              />
            </>
          )}
        </div>

        <div className="bg-surface rounded-xl shadow-lg border border-line p-6">
          {loadingBarTeam ? (
            <div className="animate-pulse">
              <div className="h-6 w-48 rounded bg-surface-2 mb-6"></div>
              <div className="h-[300px] w-full rounded-lg bg-surface-2"></div>
            </div>
          ) : (
            <>
              <h3 className="text-lg font-medium text-content mb-6">Rendimiento de Equipos</h3>
              <BarChartMulti
                data={teamPerformance}
                height={300}
                xKey="name"
                barSize={120}
                barCategoryGap="30%"
                series={[
                  { dataKey: 'completadas', name: 'Completadas', color: '#10B981' },
                  { dataKey: 'tareas', name: 'Total Tareas', color: '#3B82F6' }
                ]}
              />
            </>
          )}
        </div>
      </div>

      <div data-tour="home-types" className="bg-surface rounded-xl shadow-lg border border-line p-6 mb-8">
        {loadingBarType ? (
          <div className="animate-pulse">
            <div className="h-6 w-36 rounded bg-surface-2 mb-6"></div>
            <div className="h-[300px] w-full rounded-lg bg-surface-2"></div>
          </div>
        ) : (
          <>
            <h3 className="text-lg font-medium text-content mb-6">Tareas por Tipo</h3>
            <BarChartMulti
              data={taskTypeCounts}
              height={300}
            mobileHeight={250}
            xKey="displayName"
            xAxisHeight={50}
            xTickMaxChars={15}
            xTickMaxLines={3}
            xTickDy={10}
            xTickMobileAngle={-90}
            xTickMobileHeight={100}
            xTickMobileMaxChars={12}
            emptyMessage="No hay tareas en el período seleccionado"
            marginTop={20}
            marginBottom={0}
            barSize={taskTypeCounts.length === 1 ? 80 : null}
            series={[
              { dataKey: 'count', name: 'Cantidad de Tareas', color: getDataColor(0) }
              ]}
              showLegend={false}
            />
          </>
        )}
      </div>

      <div data-tour="home-map">
        {loadingMap ? (
          <div className="bg-surface rounded-xl shadow-lg border border-line p-6 mb-8 animate-pulse">
            <div className="h-5 w-40 rounded bg-surface-2 mb-4"></div>
            <div className="h-[360px] w-full rounded-lg bg-surface-2"></div>
          </div>
        ) : (
          <DensityMap
            data={dataDensityMap.heatMapPoints}
            center={{ lat: -31.5378028, lng: -68.5245864 }}
            zoom={12}
            radius={30}
            opacity={0.7}
          />
        )}
      </div>

      <div data-tour="home-ranking" className="rounded-xl shadow-lg md:bg-surface">
        {loadingRanking ? (
          <div className="animate-pulse">
            <div className="h-6 w-40 rounded bg-surface-2 mb-6"></div>
            <div className="space-y-3 overflow-x-auto">
              {Array.from({ length: 5 }).map((_, index) => (
                <div key={`ranking-skeleton-${index}`} className="h-10 w-full rounded bg-surface-2"></div>
              ))}
            </div>
          </div>
        ) : (
          <>
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg font-medium text-content">Ranking de Equipos</h3>
            </div>

            <div className="overflow-x-auto">
              {(() => {
              const teamsWithTasks = rankingTeam
                .filter(team => team.totalTasks > 0)
                .sort((a, b) => b.rate - a.rate);

              if (teamsWithTasks.length === 0) {
                return <p className="text-content-muted">No hay datos de equipos disponibles</p>;
              }

              return (
                <>
                  <div className="space-y-3 md:hidden">
                    {teamsWithTasks.map((team, index) => {
                      const isFirst = index === 0;
                      const isLast = index === teamsWithTasks.length - 1 && teamsWithTasks.length > 1;

                      return (
                        <div
                          key={`${team.teamId}-${index}`}
                          className="rounded-xl border border-line bg-bg/50 p-4 shadow-sm"
                        >
                          <div className="flex items-start justify-between gap-3">
                            <div className="flex min-w-0 items-center gap-3">
                              <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-surface-2 text-sm font-bold text-content">
                                {isFirst ? <CrownSimpleIcon size={18} weight="fill" className="text-amber" /> : index + 1}
                              </div>
                              <h4 className="min-w-0 break-words text-base font-semibold leading-tight text-content">
                                {team.teamName}
                              </h4>
                            </div>
                            <span className={`flex-shrink-0 text-sm font-semibold ${
                              isFirst ? 'text-success' : isLast ? 'text-error' : 'text-content'
                            }`}>
                              {team.rate}%
                            </span>
                          </div>

                          <div className="mt-4 grid grid-cols-2 gap-3 border-t border-line pt-3">
                            <div>
                              <span className="text-[11px] font-semibold uppercase tracking-wide text-content-muted">Tareas</span>
                              <p className="mt-1 text-sm font-medium text-content">
                                {team.completedTasks}/{team.totalTasks}
                              </p>
                            </div>
                            <div>
                              <span className="text-[11px] font-semibold uppercase tracking-wide text-content-muted">Eficiencia</span>
                              <p className="mt-1 text-sm font-medium text-content">{team.rate}%</p>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>

                  <table className="hidden min-w-full md:table">
                  <thead className="bg-table-head">
                    <tr className="border-b border-line">
                      <th className="text-center py-3 px-4 text-content-muted font-medium w-20">Posición</th>
                      <th className="text-left py-3 px-4 text-content-muted font-medium">Equipo</th>
                      <th className="text-center py-3 px-4 text-content-muted font-medium">Tareas</th>
                      <th className="text-center py-3 px-4 text-content-muted font-medium">Eficiencia</th>
                    </tr>
                  </thead>
                  <tbody>
                    {teamsWithTasks.map((team, index) => (
                      <tr key={`${team.teamId}-${index}`} className="border-b border-line hover:bg-[var(--app-row-hover)]">
                        <td className="py-3 px-4 text-content-muted text-center w-20">
                          {index === 0 ? (
                            <CrownSimpleIcon size={18} weight="fill" className="inline-block align-middle text-amber" />
                          ) : (
                            index + 1
                          )}
                        </td>
                        <td className="py-3 px-4 text-content font-medium">{team.teamName}</td>
                        <td className="py-3 px-4 text-content-muted text-center">{team.completedTasks}/{team.totalTasks}</td>
                        <td className="py-3 px-4 text-content-muted text-center">
                          {index === 0 && <span className="inline-block w-2.5 h-2.5 rounded-full bg-success mr-2"></span>}
                          {index === teamsWithTasks.length - 1 && teamsWithTasks.length > 1 && <span className="inline-block w-2.5 h-2.5 rounded-full bg-error mr-2"></span>}
                          {team.rate}%
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  </table>
                </>
              );
              })()}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default AdminHome;



